import { useState, type SyntheticEvent } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Switch,
    FormControlLabel,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tab,
    Tabs,
    Select,
    MenuItem,
    LinearProgress,
    type SelectChangeEvent,
} from '@mui/material'
import {
    SmartToy,
    Schedule,
    PlayArrow,
    Delete,
    Upgrade,
    History,
    Analytics,
    CheckCircle,
    Error as ErrorIcon,
    HourglassEmpty,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import {
    AI_PRICING,
    DASHBOARD_LINKS,
    isPaidPlan,
    formatCurrency,
    runTierPrice,
    type TenantPlan,
    type SupportedCurrency,
} from '@yourownmetric/shared'

import {
    useGetAgentConfigsQuery,
    useGetAgentReportsQuery,
    useCreateAgentConfigMutation,
    useUpdateAgentConfigMutation,
    useDeleteAgentConfigMutation,
    useTriggerAgentRunMutation,
    useGetAgentUsageQuery,
    type AgentConfigRow,
    type AgentReportRow,
} from '@/apis/agentsApi'
import { useGetSubscriptionQuery, useToggleAiAddonMutation } from '@/apis/billingApi'
import { useAppSelector } from '@/app/hooks'
import { EmptyState } from '@/components/shared/EmptyState'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'

// ── Constants ─────────────────────────────────────────────────

const AGENT_TYPE_INFO: Record<string, { label: string; description: string; icon: string }> = {
    anomaly_detection: {
        label: 'Anomaly Detection',
        description: 'Detects unusual traffic patterns and alerts you.',
        icon: '🔍',
    },
    daily_summary: {
        label: 'Daily Summary',
        description: 'AI-generated daily analytics summary.',
        icon: '📊',
    },
    weekly_trends: {
        label: 'Weekly Trends',
        description: 'Identifies week-over-week trends and insights.',
        icon: '📈',
    },
    funnel_optimizer: {
        label: 'Funnel Optimizer',
        description: 'Analyzes conversion funnels and suggests improvements.',
        icon: '🎯',
    },
    cohort_analyzer: {
        label: 'Cohort Analyzer',
        description: 'Segments users and tracks cohort behavior.',
        icon: '👥',
    },
    revenue_attribution: {
        label: 'Revenue Attribution',
        description: 'Attributes revenue to traffic sources.',
        icon: '💰',
    },
    executive_summary: {
        label: 'Executive Summary',
        description: 'Monthly executive-level analytics report.',
        icon: '📋',
    },
    nl_query: {
        label: 'Natural Language Query',
        description: 'Ask questions about your data in plain English.',
        icon: '💬',
    },
}

type ChipColor = 'success' | 'warning' | 'error' | 'default'

const STATUS_CHIP: Record<string, ChipColor> = {
    completed: 'success',
    running: 'warning',
    pending: 'default',
    failed: 'error',
}

const STATUS_ICON: Record<string, React.ReactNode> = {
    completed: <CheckCircle fontSize="small" color="success" />,
    running: <CircularProgress size={16} />,
    pending: <HourglassEmpty fontSize="small" color="disabled" />,
    failed: <ErrorIcon fontSize="small" color="error" />,
}

const SCHEDULE_OPTIONS = [
    { value: '', label: 'Manual only' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
]

// ── Agents Tab ────────────────────────────────────────────────

interface AgentsTabProps {
    configs: AgentConfigRow[]
    selectedProjectId: string | null
    needsPaidPlan: boolean
    needsAddon: boolean
    triggering: boolean
    currency: SupportedCurrency
    onToggle: (agentType: string) => void
    onTrigger: (agentType: string) => void
    onDelete: (id: string) => void
    onScheduleChange: (configId: string, schedule: string) => void
    onAddonDialog: () => void
}

function AgentsTab({
    configs,
    selectedProjectId,
    needsPaidPlan,
    needsAddon,
    triggering,
    currency,
    onToggle,
    onTrigger,
    onDelete,
    onScheduleChange,
    onAddonDialog,
}: AgentsTabProps) {
    const navigate = useNavigate()
    const fmt = (cents: number) => formatCurrency(cents, currency)
    const allAgentTypes = Object.keys(AGENT_TYPE_INFO)
    const configMap = new Map(configs.map(c => [c.agentType, c]))

    return (
        <Box>
            {!selectedProjectId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Select a project from the top bar to configure agents.
                </Alert>
            )}

            {(needsPaidPlan || needsAddon) && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        needsPaidPlan ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => navigate(DASHBOARD_LINKS.billing)}
                            >
                                Upgrade Plan
                            </Button>
                        ) : (
                            <Button color="inherit" size="small" onClick={onAddonDialog}>
                                Enable Add-on
                            </Button>
                        )
                    }
                >
                    {needsPaidPlan
                        ? `AI Agents require a paid plan + AI Add-on (${fmt(AI_PRICING.retainer[currency])}/mo). Upgrade to get started.`
                        : `Enable the AI Insights Add-on (${fmt(AI_PRICING.retainer[currency])}/mo) to start using AI agents.`}
                </Alert>
            )}

            <Grid container spacing={2}>
                {allAgentTypes.map(type => {
                    const info = AGENT_TYPE_INFO[type]
                    const config = configMap.get(type)
                    const enabled = config?.enabled === 1

                    return (
                        <Grid key={type} size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card sx={{ height: '100%', opacity: enabled ? 1 : 0.7 }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ lineHeight: 1 }}>
                                            {info.icon}
                                        </Typography>
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            {enabled && (
                                                <Tooltip title="Run now">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onTrigger(type)}
                                                        disabled={triggering}
                                                    >
                                                        {triggering ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <PlayArrow fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {config && (
                                                <Tooltip title="Delete config">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => onDelete(config.id)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={enabled}
                                                        size="small"
                                                        onChange={() => onToggle(type)}
                                                        disabled={!selectedProjectId}
                                                    />
                                                }
                                                label=""
                                            />
                                        </Box>
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                                        {info.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {info.description}
                                    </Typography>

                                    {/* Schedule selector */}
                                    {enabled && type !== 'nl_query' && (
                                        <Select
                                            size="small"
                                            value={config?.schedule ?? ''}
                                            onChange={(e: SelectChangeEvent) =>
                                                onScheduleChange(config!.id, e.target.value)
                                            }
                                            sx={{ mt: 1, minWidth: 130 }}
                                            displayEmpty
                                        >
                                            {SCHEDULE_OPTIONS.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    <Schedule
                                                        sx={{
                                                            fontSize: 14,
                                                            mr: 0.5,
                                                            verticalAlign: 'text-bottom',
                                                        }}
                                                    />
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}

                                    {(needsPaidPlan || needsAddon) && (
                                        <Chip
                                            label="Requires AI Add-on"
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            sx={{ mt: 1, mr: 0.5 }}
                                        />
                                    )}

                                    {/* Last run status */}
                                    {config?.lastRunStatus && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                mt: 1,
                                            }}
                                        >
                                            {STATUS_ICON[config.lastRunStatus] ?? null}
                                            <Typography variant="caption" color="text.secondary">
                                                Last run:{' '}
                                                {config.lastRunAt
                                                    ? new Date(config.lastRunAt).toLocaleString()
                                                    : '—'}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>
        </Box>
    )
}

// ── Run History Tab ───────────────────────────────────────────

interface RunHistoryTabProps {
    reports: AgentReportRow[]
}

function RunHistoryTab({ reports }: RunHistoryTabProps) {
    if (reports.length === 0) {
        return (
            <EmptyState
                icon={<History />}
                title="No runs yet"
                description="Enable an AI agent and trigger a run to see results here."
            />
        )
    }

    return (
        <Card>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Agent</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Trigger</TableCell>
                        <TableCell>Tokens</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Completed</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reports.map(r => {
                        const info = AGENT_TYPE_INFO[r.agentType]
                        const chipColor = STATUS_CHIP[r.status] ?? 'default'

                        return (
                            <TableRow key={r.id}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {info?.icon} {info?.label ?? r.agentType}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={r.status}
                                        color={chipColor}
                                        size="small"
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={
                                            r.triggerType === 'scheduled' ? 'Scheduled' : 'Manual'
                                        }
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>{r.tokensUsed.toLocaleString()}</TableCell>
                                <TableCell>{new Date(r.createdAt).toLocaleString()}</TableCell>
                                <TableCell>
                                    {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </Card>
    )
}

// ── Usage Tab ─────────────────────────────────────────────────

function UsageTab() {
    const { data: usage, isLoading } = useGetAgentUsageQuery()
    const currency: SupportedCurrency = usage?.currency ?? 'USD'
    const fmt = (cents: number) => formatCurrency(cents, currency)

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!usage) {
        return (
            <EmptyState
                icon={<Analytics />}
                title="No usage data"
                description="Enable the AI Add-on and run some agents to see usage data."
            />
        )
    }

    const runProgress = Math.min(100, (usage.totalRuns / Math.max(1, usage.includedRuns)) * 100)
    const overage = Math.max(0, usage.totalRuns - usage.includedRuns)

    return (
        <Box>
            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total Runs
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                                {usage.totalRuns}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                of {usage.includedRuns} included
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={runProgress}
                                color={runProgress > 90 ? 'warning' : 'primary'}
                                sx={{ mt: 1, borderRadius: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Overage Runs
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                                {overage}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                billed per-run
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Retainer
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                                {fmt(usage.retainerCents)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                monthly base
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total AI Cost
                            </Typography>
                            <Typography variant="h4" fontWeight={700} color="primary">
                                {fmt(usage.totalCostCents)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                this billing period
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Per-agent breakdown */}
            {usage.breakdown.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Per-Agent Breakdown
                    </Typography>
                    <Card>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Agent</TableCell>
                                    <TableCell align="right">Runs</TableCell>
                                    <TableCell align="right">Success</TableCell>
                                    <TableCell align="right">Failed</TableCell>
                                    <TableCell align="right">Tokens Used</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usage.breakdown.map(row => {
                                    const info = AGENT_TYPE_INFO[row.agentType]
                                    return (
                                        <TableRow key={row.agentType}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {info?.icon} {info?.label ?? row.agentType}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">{row.runCount}</TableCell>
                                            <TableCell align="right">{row.successCount}</TableCell>
                                            <TableCell align="right">{row.failedCount}</TableCell>
                                            <TableCell align="right">
                                                {row.totalTokens.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Card>
                </>
            )}

            {/* Pricing tiers */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                AI Run Pricing Tiers
            </Typography>
            <Card>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Tier</TableCell>
                            <TableCell>Runs</TableCell>
                            <TableCell align="right">Per Run</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {AI_PRICING.runTiers.map((tier, i) => {
                            const prev = i > 0 ? AI_PRICING.runTiers[i - 1].upTo : 0
                            const rangeLabel =
                                tier.upTo === Infinity
                                    ? `${prev + 1}+`
                                    : `${prev + 1} – ${tier.upTo}`
                            return (
                                <TableRow key={tier.label}>
                                    <TableCell>{tier.label}</TableCell>
                                    <TableCell>{rangeLabel}</TableCell>
                                    <TableCell align="right">
                                        {runTierPrice(tier, currency) === 0
                                            ? 'Free'
                                            : fmt(runTierPrice(tier, currency))}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </Card>
        </Box>
    )
}

// ── Main Page ─────────────────────────────────────────────────

export function AgentsPage() {
    const selectedProjectId = useAppSelector(s => s.project.selectedId)
    const navigate = useNavigate()

    const { data: subscription } = useGetSubscriptionQuery()
    const { data: configs = [] } = useGetAgentConfigsQuery()
    const { data: reports = [] } = useGetAgentReportsQuery({ limit: 50 })
    const [createConfig] = useCreateAgentConfigMutation()
    const [updateConfig] = useUpdateAgentConfigMutation()
    const [deleteConfig] = useDeleteAgentConfigMutation()
    const [triggerRun, { isLoading: triggering }] = useTriggerAgentRunMutation()
    const [toggleAiAddon] = useToggleAiAddonMutation()

    const [tab, setTab] = useState(0)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [deleteId, setDeleteId] = useState<string | null>(null)
    const [addonDialogOpen, setAddonDialogOpen] = useState(false)

    const currentPlan = (subscription?.plan ?? 'free') as TenantPlan
    const aiAddonEnabled = !!subscription?.aiAddon
    const needsPaidPlan = !isPaidPlan(currentPlan)
    const needsAddon = !needsPaidPlan && !aiAddonEnabled
    const currency: SupportedCurrency = subscription?.currency ?? 'USD'
    const fmt = (cents: number) => formatCurrency(cents, currency)

    const configMap = new Map(configs.map(c => [c.agentType, c]))

    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        setTab(newValue)
    }

    const handleToggle = async (agentType: string) => {
        setError('')
        if (!selectedProjectId) {
            setError('Please select a project first')
            return
        }

        if (needsPaidPlan || needsAddon) {
            setAddonDialogOpen(true)
            return
        }

        try {
            const existing = configMap.get(agentType)
            if (existing) {
                await updateConfig({
                    id: existing.id,
                    enabled: existing.enabled !== 1,
                }).unwrap()
                setSuccess(
                    `${AGENT_TYPE_INFO[agentType].label} ${existing.enabled === 1 ? 'disabled' : 'enabled'}`,
                )
            } else {
                await createConfig({
                    projectId: selectedProjectId,
                    agentType,
                    enabled: true,
                }).unwrap()
                setSuccess(`${AGENT_TYPE_INFO[agentType].label} enabled`)
            }
        } catch {
            setError('Failed to update agent config')
        }
    }

    const handleScheduleChange = async (configId: string, schedule: string) => {
        setError('')
        try {
            await updateConfig({ id: configId, schedule: schedule || undefined }).unwrap()
            setSuccess(`Schedule updated to ${schedule || 'manual'}`)
        } catch {
            setError('Failed to update schedule')
        }
    }

    const handleEnableAddon = async () => {
        setError('')
        setAddonDialogOpen(false)
        try {
            await toggleAiAddon({ enabled: true }).unwrap()
            setSuccess('AI Add-on enabled! You can now configure agents.')
        } catch {
            setError('Failed to enable AI Add-on')
        }
    }

    const handleTrigger = async (agentType: string) => {
        setError('')
        if (!selectedProjectId) {
            setError('Please select a project first')
            return
        }
        try {
            const config = configMap.get(agentType)
            await triggerRun({
                projectId: selectedProjectId,
                agentType,
                agentConfigId: config?.id,
            }).unwrap()
            setSuccess(`${AGENT_TYPE_INFO[agentType].label} run triggered`)
        } catch {
            setError('Failed to trigger agent run')
        }
    }

    const handleDelete = async () => {
        if (!deleteId) {
            return
        }
        try {
            await deleteConfig(deleteId).unwrap()
            setDeleteId(null)
            setSuccess('Agent config deleted')
        } catch {
            setError('Failed to delete agent config')
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                AI Agents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure AI-powered analytics agents. Available with the AI Add-on (
                {fmt(AI_PRICING.retainer[currency])}/mo).
            </Typography>

            {error && (
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
                <Tab icon={<SmartToy />} iconPosition="start" label="Agents" />
                <Tab icon={<History />} iconPosition="start" label="Run History" />
                <Tab icon={<Analytics />} iconPosition="start" label="Usage" />
            </Tabs>

            {tab === 0 && (
                <AgentsTab
                    configs={configs}
                    selectedProjectId={selectedProjectId}
                    needsPaidPlan={needsPaidPlan}
                    needsAddon={needsAddon}
                    triggering={triggering}
                    currency={currency}
                    onToggle={handleToggle}
                    onTrigger={handleTrigger}
                    onDelete={id => setDeleteId(id)}
                    onScheduleChange={handleScheduleChange}
                    onAddonDialog={() => setAddonDialogOpen(true)}
                />
            )}
            {tab === 1 && <RunHistoryTab reports={reports} />}
            {tab === 2 && <UsageTab />}

            {/* AI Add-on / Upgrade Dialog */}
            <Dialog
                open={addonDialogOpen}
                onClose={() => setAddonDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <SmartToy sx={{ fontSize: 20, mr: 1, verticalAlign: 'text-bottom' }} />
                    {needsPaidPlan ? 'Upgrade Required' : 'Enable AI Add-on'}
                </DialogTitle>
                <DialogContent>
                    {needsPaidPlan ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                AI Agents require pay-as-you-go billing and the AI Insights Add-on (
                                {fmt(AI_PRICING.retainer[currency])}/mo).
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Go to the Billing page to activate pay-as-you-go, then enable the AI
                                Add-on.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2">
                            Enable the AI Insights Add-on ({fmt(AI_PRICING.retainer[currency])}
                            /mo) to unlock {AI_PRICING.agents.length} AI-powered analytics agents.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddonDialogOpen(false)}>Cancel</Button>
                    {needsPaidPlan ? (
                        <Button
                            variant="contained"
                            startIcon={<Upgrade />}
                            onClick={() => {
                                setAddonDialogOpen(false)
                                navigate(DASHBOARD_LINKS.billing)
                            }}
                        >
                            Go to Billing
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleEnableAddon}>
                            Enable Add-on ({fmt(AI_PRICING.retainer[currency])}/mo)
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!deleteId}
                title="Delete Agent Config?"
                message="This will remove the agent configuration. You can re-create it later."
                confirmColor="error"
                onConfirm={handleDelete}
                onCancel={() => setDeleteId(null)}
            />
        </Box>
    )
}
